import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { environment } from 'src/environments/environment';
import { CheckboxComponent } from 'src/app/shared/ui-common/checkbox/checkbox.component';
import {
  SelectComponent,
  SelectOption,
} from 'src/app/shared/ui-common/select/select.component';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { distinctUntilChanged, skip, Subscription } from 'rxjs';
import { Settings, SettingsService } from '../data/settings.service';
import { SharedService } from 'src/app/shared/data/shared.service';
import { TranslatePipe } from 'src/app/shared/data/pipes/translate.pipe';
import { TranslationService } from 'src/app/shared/data/translation.service';
import { Router, RouterModule } from '@angular/router';
import { FeaturePillMenuComponent } from 'src/app/shared/feature-pill-menu/feature-pill-menu.component';
import { PillComponent } from 'src/app/shared/ui-common/pill/pill.component';
import { PillService } from 'src/app/shared/data/pill.service';

@Component({
  selector: 'app-feature-settings',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CheckboxComponent,
    SelectComponent,
    TranslatePipe,
    RouterModule,
    PillComponent,
    FeaturePillMenuComponent
  ],
  templateUrl: './feature-settings.component.html',
  styleUrl: './feature-settings.component.scss',
})
export class FeatureSettingsComponent implements OnInit, OnDestroy {

  private formBuilder = inject(FormBuilder);
  private settingService = inject(SettingsService);
  private formValueSubscription!: Subscription;
  private danishLanguageId = '1041';

  languageLoading = false;
  pillExpanded = false;
  settings?: Settings;
  s3BaseUrl = environment.s3BaseUrl;
  companyLogo = signal<string>('');
  form: FormGroup = this.formBuilder.group({
    notifyBySMS: [true],
    notifyByEmail: [false],
    notifyByPush: [true],
    userLanguage: [this.danishLanguageId],
  });

  get fullname(): string {
    return this.settings?.fullname ?? '';
  }

  get languages(): SelectOption[] {
    if (!this.settings?.languages) {
      return [];
    }

    const selectedLanguageId = this.form.value.userLanguage;
    const mappedLanguages = this.settings.languages.map(({ id, englishName }) => ({
      value: String(id),
      label: englishName,
    }));

    const selectedLanguage = mappedLanguages.find(
      (lang) => lang.value === selectedLanguageId
    );
    const otherLanguages = mappedLanguages.filter(
      (lang) => lang.value !== selectedLanguageId
    );

    otherLanguages.sort((a, b) => a.label.localeCompare(b.label));

    return selectedLanguage
      ? [selectedLanguage, ...otherLanguages]
      : otherLanguages;
  }

  get nextPushNotificationDate(): Date | null {
    return this.settings?.nextPushNotificationDate
      ? new Date(this.settings?.nextPushNotificationDate)
      : null;
  }

  get nextSMSNotificationDate(): Date | null {
    return this.settings?.nextSMSNotificationDate
      ? new Date(this.settings?.nextSMSNotificationDate)
      : null;
  }

  get nextEmailNotificationDate(): Date | null {
    return this.settings?.nextEmailNotificationDate
      ? new Date(this.settings?.nextEmailNotificationDate)
      : null;
  }

  get isHowdyLogo(): boolean {
    return this.companyLogo().includes('HRONWellbeing');
  }

  constructor(
    private sharedService: SharedService,
    private translationService: TranslationService,
    private router: Router,
    private location: Location,
    private pillService: PillService
  ) {}

  ngOnInit(): void {
    this.companyLogo.set(this.sharedService.getCompanyLogoUrl(true));
    this.settingService.settings$.subscribe((data) => {
      if (data) {
        this.settings = data;
        this.setFormValues();
      }
    });

    this.formValueSubscription = this.form.valueChanges
      .pipe(skip(1), distinctUntilChanged())
      .subscribe(() => {
        this.onSubmit();
      });
  }

  ngOnDestroy(): void {
    this.formValueSubscription.unsubscribe();
  }

  getHowdyLogo(): void {
    this.companyLogo.set(this.sharedService.getHowdyLogoUrl(true));
  }

  goBack(): void {
    this.location.back();
  }

  onSubmit(): void {
    if (!this.form.dirty || !this.form.valid) {
      return;
    }

    const newLanguageId = this.form.value.userLanguage;
    const previousLanguageId = this.settings?.languageId;

    this.languageLoading = previousLanguageId !== +newLanguageId;
    this.settingService.updateSettings(this.form.value).subscribe(async updatedSettings => {
      if (this.settings) {
        this.settings.nextEmailNotificationDate = updatedSettings.nextEmailNotificationDate;
        this.settings.nextPushNotificationDate = updatedSettings.nextPushNotificationDate;
        this.settings.nextSMSNotificationDate = updatedSettings.nextSMSNotificationDate;
        this.settings.languageId = +newLanguageId;
      }

      if (previousLanguageId !== newLanguageId) {
        await this.translationService.refreshTranslationsFromApi(newLanguageId);
      }

      this.pillService.loadMenuItems(true);
      this.languageLoading = false;
    });
  }

  goToMenu(): void {
    this.router.navigate(['/available-surveys']);
  }

  private setFormValues(): void {
    this.form.patchValue({
      notifyBySMS: this.settings?.notifyBySMS ?? false,
      notifyByEmail: this.settings?.notifyByEmail ?? false,
      notifyByPush: this.settings?.notifyByPush ?? false,
      userLanguage: this.settings?.languageId
        ? `${this.settings?.languageId}`
        : this.danishLanguageId,
    });

    if (!this.settings?.notifyBySMSEnabled) {
      this.form.get('notifyBySMS')?.disable();
    }

    if (!this.settings?.notifyByEmailEnabled) {
      this.form.get('notifyByEmail')?.disable();
    }

    if (!this.settings?.notifyByPushEnabled) {
      this.form.get('notifyByPush')?.disable();
    }

    if (!this.settings?.changeLanguageEnabled) {
      this.form.get('userLanguage')?.disable();
    }
  }
}
